import { useEffect, useRef } from 'react';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

function reveal(isVisible: boolean, extra = '') {
  return `fade-up ${isVisible ? 'revealed' : ''} ${extra}`.trim();
}

function stagger(i: number): React.CSSProperties {
  return { '--stagger': i } as React.CSSProperties;
}

export function VisionSection() {
  const [ref, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  // Roadmap animation
  const roadmapRef = useRef<HTMLDivElement>(null);
  const hasAnimatedRoadmap = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimatedRoadmap.current) return;
    hasAnimatedRoadmap.current = true;

    const roadmap = roadmapRef.current;
    if (!roadmap) return;

    const progressBar = roadmap.querySelector('.roadmap-progress');
    const phases = roadmap.querySelectorAll('.roadmap-phase');

    if (progressBar) progressBar.classList.add('animated');
    phases.forEach((phase, i) => {
      setTimeout(() => phase.classList.add('active'), 500 + i * 400);
    });
  }, [isVisible]);

  // Flywheel animation
  const flywheelRef = useRef<HTMLDivElement>(null);
  const hasAnimatedFlywheel = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimatedFlywheel.current) return;
    hasAnimatedFlywheel.current = true;

    const wrapper = flywheelRef.current;
    if (!wrapper) return;

    const ring = wrapper.querySelector('.flywheel-ring-animated');
    const arrows = wrapper.querySelectorAll('.flywheel-arrow');

    if (ring) ring.classList.add('animated');
    arrows.forEach((arrow, i) => {
      setTimeout(() => arrow.classList.add('visible'), 1000 + i * 300);
    });
  }, [isVisible]);

  return (
    <section id="vision" className="section vision-section" aria-label="Long-Term Vision" ref={ref}>
      <div className="section-inner">
        <h2 className={reveal(isVisible, 'section-title')}>The Vision</h2>
        <p className={reveal(isVisible, 'section-subtitle')} style={stagger(1)}>We are building an AI company. Not an edtech company.</p>

        {/* Roadmap */}
        <div className={reveal(isVisible, 'vision-roadmap')} style={stagger(2)} ref={roadmapRef}>
          <div className="roadmap-line">
            <div className="roadmap-progress"></div>
          </div>
          <div className="roadmap-phases" data-stagger>
            <div className={reveal(isVisible, 'roadmap-phase')}>
              <div className="roadmap-node">
                <span className="material-symbols-outlined">rocket_launch</span>
              </div>
              <div className="roadmap-card">
                <md-elevation></md-elevation>
                <h4>Scaffold</h4>
                <span className="roadmap-label">Prove the Thesis</span>
                <p>A 16-week coding bootcamp that proves AI-powered, neuroscience-grounded learning produces measurably better outcomes. Quantified evidence, not faith.</p>
              </div>
            </div>
            <div className={reveal(isVisible, 'roadmap-phase')}>
              <div className="roadmap-node">
                <span className="material-symbols-outlined">hub</span>
              </div>
              <div className="roadmap-card">
                <md-elevation></md-elevation>
                <h4>Platform</h4>
                <span className="roadmap-label">Extract &amp; Scale</span>
                <p>Extract the learning engine into a platform any institution can use. Universities, corporations, training providers, any subject, all powered by the same infrastructure.</p>
              </div>
            </div>
            <div className={reveal(isVisible, 'roadmap-phase')}>
              <div className="roadmap-node">
                <span className="material-symbols-outlined">public</span>
              </div>
              <div className="roadmap-card">
                <md-elevation></md-elevation>
                <h4>Infrastructure</h4>
                <span className="roadmap-label">The Learning Layer</span>
                <p>Become what Stripe is to payments: the infrastructure layer for learning. Content creators, institutions, and employers connect through a shared system that optimizes how humanity learns.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Flywheel */}
        <div className={reveal(isVisible, 'data-flywheel-wrapper')} ref={flywheelRef}>
          <h3 className="flywheel-title">The Data Flywheel</h3>
          <div className="flywheel-layout">
            <div className="data-flywheel">
              <svg className="flywheel-svg" viewBox="-40 -20 480 440" aria-label="Data flywheel: Learners create signals, AI improves, better outcomes attract more learners">
                {/* Outer ring */}
                <circle className="flywheel-ring" cx="200" cy="200" r="160" fill="none" stroke="var(--md-sys-color-primary-container)" strokeWidth="40" opacity="0.3" />
                {/* Animated ring */}
                <circle className="flywheel-ring-animated" cx="200" cy="200" r="160" fill="none" stroke="var(--md-sys-color-primary)" strokeWidth="4" />
                {/* Center */}
                <circle cx="200" cy="200" r="50" fill="var(--md-sys-color-primary)" opacity="0.1" />
                <text x="200" y="195" textAnchor="middle" className="flywheel-center-text">Deeper</text>
                <text x="200" y="215" textAnchor="middle" className="flywheel-center-text">Moat</text>
                {/* Labels */}
                <text x="200" y="28" textAnchor="middle" className="flywheel-label">Learners</text>
                <text x="378" y="205" textAnchor="end" className="flywheel-label">Learning Signals</text>
                <text x="310" y="370" textAnchor="middle" className="flywheel-label">AI Improves</text>
                <text x="90" y="370" textAnchor="middle" className="flywheel-label">Better Outcomes</text>
                <text x="22" y="205" textAnchor="start" className="flywheel-label">More Learners</text>
                {/* Arrows */}
                <path d="M280,52 L300,42 L290,62" fill="var(--md-sys-color-primary)" className="flywheel-arrow" />
                <path d="M362,280 L372,300 L352,290" fill="var(--md-sys-color-primary)" className="flywheel-arrow" />
                <path d="M200,362 L190,380 L210,380" fill="var(--md-sys-color-primary)" className="flywheel-arrow" />
                <path d="M38,280 L28,300 L48,290" fill="var(--md-sys-color-primary)" className="flywheel-arrow" />
                <path d="M120,52 L100,42 L110,62" fill="var(--md-sys-color-primary)" className="flywheel-arrow" />
              </svg>
            </div>
            <div className="flywheel-stages">
              {[
                { num: 1, title: 'Learners', desc: 'Students engage with active, struggle-based learning' },
                { num: 2, title: 'Learning Signals', desc: 'Process assessment captures reasoning patterns, not just answers' },
                { num: 3, title: 'AI Improves', desc: 'Every interaction trains a smarter, more adaptive Scout' },
                { num: 4, title: 'Better Outcomes', desc: 'Higher retention, deeper mastery, measurable cognitive growth' },
                { num: 5, title: 'More Learners', desc: 'Proven outcomes attract the next cohort. The cycle accelerates' },
              ].map(({ num, title, desc }) => (
                <div key={num} className="flywheel-stage">
                  <span className="flywheel-stage-num">{num}</span>
                  <div className="flywheel-stage-content">
                    <strong>{title}</strong>
                    <span>{desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className={reveal(isVisible, 'vision-message')}>
          Every learner makes the system smarter. Every interaction deepens the moat.
        </p>
      </div>
    </section>
  );
}
